import {useState} from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {FeedbackFormCheckboxSet} from 'shells/billing/feedback-page/feedback-form-checkbox-set';
import {FeedbackFormWrapper} from 'shells/billing/feedback-page/feedback-form-wrapper';
import {Button} from 'shells/button/button';
import {CheckboxGroup} from 'shells/checkbox/checkbox-group';
import {FormField} from 'shells/form/form-field';
import {Label} from 'shells/form/label';
import {Stack} from 'shells/layout/stack/stack';
import {Link} from 'shells/link/link';
import {Radio} from 'shells/radio/radio';
import {RadioGroup} from 'shells/radio/radio-group';
import {SelectPickerGeneric} from 'shells/select-picker-generic/select-picker-generic';
import {TextField} from 'shells/textfield/textfield';
import {Body} from 'shells/typography/body';

import {
    cancelReasonCategories,
    crmOptions,
    INTEGRATION_REASON,
    recommendScoreOptions,
} from './consts';
import type {CancelFormData, CrmOption} from './types';

const crmSelectOptions = crmOptions.map((crm) => ({value: crm, label: crm}));

interface Props {
    onSubmit: (data: CancelFormData) => void;
    isSubmitting: boolean;
    error: string | null | undefined;
}

export function CancelForm(props: Props) {
    const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
    const [additionalComments, setAdditionalComments] = useState<string>('');
    const [isSwitchingCrm, setIsSwitchingCrm] = useState<string | null | undefined>(null);
    const [selectedCrm, setSelectedCrm] = useState<CrmOption | null | undefined>(null);
    const [recommendScore, setRecommendScore] = useState<string | null | undefined>(null);
    const [whatWouldMakeYouStay, setWhatWouldMakeYouStay] = useState<string>('');
    const [additionalFeedback, setAdditionalFeedback] = useState<string>('');
    const [whatSystemAreYouTryingToConnectWith, setWhatSystemAreYouTryingToConnectWith] =
        useState<string>('');

    const followUpValues: Record<string, string> = {
        whatSystemAreYouTryingToConnectWith,
    };

    const handleFollowUpChange = (id: string, value: string) => {
        if (id === 'whatSystemAreYouTryingToConnectWith') {
            setWhatSystemAreYouTryingToConnectWith(value);
        }
    };

    const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault();

        const formData: CancelFormData = {
            mainReasons: selectedReasons,
            otherReason: additionalComments.trim(),
            areYouSwitching: isSwitchingCrm || '',
            whichCRM: selectedCrm ? selectedCrm.value : null,
            likelyToRecommend: recommendScore || '',
            whatWouldMakeYouStay: whatWouldMakeYouStay.trim(),
            additionalFeedback: additionalFeedback.trim(),
            whatSystemAreYouTryingToConnectWith: whatSystemAreYouTryingToConnectWith.trim(),
        };

        props.onSubmit(formData);
    };

    const submitButtonDisabled =
        props.isSubmitting ||
        (selectedReasons.length === 0 && additionalComments.trim().length === 0) ||
        isSwitchingCrm === null ||
        (isSwitchingCrm === 'yes' && !selectedCrm) ||
        (selectedReasons.includes(INTEGRATION_REASON) &&
            whatSystemAreYouTryingToConnectWith.trim().length === 0) ||
        recommendScore === null ||
        whatWouldMakeYouStay.trim().length === 0;

    return (
        <FeedbackFormWrapper onSubmit={handleSubmit}>
            <Stack spacing={48} alignItems='start' isFullWidth={true}>
                <Stack spacing={32}>
                    <Stack spacing={8}>
                        <Label htmlFor='churn-reason' required={true} largeSize={true}>
                            Main reasons for canceling (select all that apply):
                        </Label>
                        <CheckboxGroup
                            name='churn-reason'
                            selectedValues={selectedReasons}
                            onChange={setSelectedReasons}
                        >
                            <Stack spacing={32}>
                                {cancelReasonCategories.map((category) => (
                                    <FeedbackFormCheckboxSet
                                        key={category.title}
                                        title={category.title}
                                        options={category.options}
                                        selectedValues={selectedReasons}
                                        followUpValues={followUpValues}
                                        onFollowUpChange={handleFollowUpChange}
                                    />
                                ))}
                            </Stack>
                        </CheckboxGroup>
                    </Stack>
                    <Stack spacing={8} alignItems='start' isFullWidth={true}>
                        <Body isBold={true}>Other</Body>
                        <TextField
                            value={additionalComments}
                            onChange={(e) => setAdditionalComments(e)}
                            multiline={true}
                            rows={4}
                            placeholder='Enter comment(s)'
                        />
                    </Stack>
                </Stack>
                <Stack spacing={16}>
                    <Stack spacing={8}>
                        <Label htmlFor='are-you-switching' required={true} largeSize={true}>
                            Are you switching to another CRM?
                        </Label>
                        <RadioGroup
                            name='are-you-switching'
                            selectedValue={isSwitchingCrm}
                            onChange={setIsSwitchingCrm}
                        >
                            <Radio value='yes'>Yes</Radio>
                            <Radio value='no'>No</Radio>
                        </RadioGroup>
                    </Stack>
                    {isSwitchingCrm === 'yes' && (
                        <Stack spacing={8} isFullWidth={true}>
                            <Label htmlFor='which-crm' required={true} largeSize={true}>
                                Which CRM?
                            </Label>
                            <SelectPickerGeneric
                                placeholder='Select a CRM'
                                value={selectedCrm}
                                onChange={setSelectedCrm}
                                onClear={() => setSelectedCrm(null)}
                                options={crmSelectOptions}
                            />
                        </Stack>
                    )}
                </Stack>
                <Stack spacing={8}>
                    <Label htmlFor='recommend-score' required={true} largeSize={true}>
                        How likely would you be to recommend Nutshell to a friend or colleague?
                    </Label>
                    <RadioGroup
                        name='recommend-score'
                        selectedValue={recommendScore}
                        onChange={setRecommendScore}
                        inline={true}
                        shouldSpaceHorizontally={true}
                    >
                        {recommendScoreOptions.map((option) => (
                            <Radio key={option} value={option}>
                                {option}
                            </Radio>
                        ))}
                    </RadioGroup>
                    <div className='flex justify-sb full-width'>
                        <Body size='small' color='light'>
                            Not likely
                        </Body>
                        <Body size='small' color='light'>
                            Very likely
                        </Body>
                    </div>
                </Stack>
                <FormField
                    innerId='what-would-make-you-stay'
                    label='What would make you stay with Nutshell?'
                    isRequired={true}
                    isFullWidth={true}
                >
                    <TextField
                        value={whatWouldMakeYouStay}
                        onChange={(e) => setWhatWouldMakeYouStay(e)}
                        multiline={true}
                        rows={4}
                        placeholder='Enter your response'
                    />
                </FormField>
                <FormField
                    innerId='additional-feedback'
                    label='Do you have any additional feedback you would be willing to share with us?'
                    isFullWidth={true}
                >
                    <TextField
                        value={additionalFeedback}
                        onChange={(e) => setAdditionalFeedback(e)}
                        multiline={true}
                        rows={4}
                        placeholder='Enter your response'
                    />
                </FormField>
                <Stack spacing={32} isFullWidth={true} alignItems='center'>
                    {props.error && <Body color='error'>{props.error}</Body>}
                    <Button
                        type='submit'
                        variant='primary'
                        size='xlarge'
                        isFullWidth={true}
                        disabled={submitButtonDisabled}
                    >
                        {props.isSubmitting ? 'Cancelling...' : 'Request to cancel my account'}
                    </Button>
                    {/* @ts-expect-error */}
                    <Link as={RoutedLink} to='/setup/billing'>
                        I changed my mind, take me back to billing
                    </Link>
                </Stack>
            </Stack>
        </FeedbackFormWrapper>
    );
}
